import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Truck,
  Package,
  Clock,
  ShieldCheck,
  MapPin,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1576793048000-494aaa93d160?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
const SERVICES_IMG =
  "https://images.unsplash.com/photo-1587813369290-091c9d432daf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: "easeOut" },
  }),
};

const features = [
  { icon: ShieldCheck, title: "Sikker transport", text: "Dine varer håndteres med omhu og fragtes sikkert frem hver gang." },
  { icon: Clock, title: "Punktlig levering", text: "Vi holder aftaler og leverer til tiden – hver eneste gang." },
  { icon: Package, title: "Fleksible løsninger", text: "Transport tilpasset netop din virksomheds behov og fragttype." },
  { icon: MapPin, title: "Hele Danmark", text: "Vi kører i hele landet med et pålideligt og effektivt netværk." },
];

const services = [
  "Stykgods og pakketransport",
  "Palleforsendelser",
  "Distribution i hele Danmark",
  "Faste kørselsaftaler",
  "Ekspres- og hastelevering",
  "Skræddersyede transportløsninger",
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Varevogn på vejen" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-slate-900/65" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 to-transparent" />
        </div>

        <div className="relative mx-auto flex min-h-[620px] max-w-7xl flex-col justify-center px-6 py-24">
          <motion.span
            initial="hidden" animate="show" variants={fadeUp}
            className="mb-6 inline-flex w-fit items-center gap-2 rounded-sm border border-white/20 bg-white/10 px-4 py-1.5 text-sm font-medium text-white backdrop-blur"
          >
            <Truck className="h-4 w-4" /> Transport & logistik i hele Danmark
          </motion.span>

          <motion.h1
            initial="hidden" animate="show" custom={1} variants={fadeUp}
            className="max-w-4xl font-heading text-4xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl"
          >
            Professionelle transportløsninger til danske virksomheder
          </motion.h1>

          <motion.p
            initial="hidden" animate="show" custom={2} variants={fadeUp}
            className="mt-6 max-w-2xl text-base leading-relaxed text-slate-200 md:text-lg"
          >
            SB Logistik leverer professionelle, fleksible og pålidelige
            transportløsninger til virksomheder i hele Danmark. Vi hjælper vores
            kunder med sikker, effektiv og punktlig transport, hvor kvalitet og
            service altid er i fokus.
          </motion.p>

          <motion.div
            initial="hidden" animate="show" custom={3} variants={fadeUp}
            className="mt-10 flex flex-col gap-4 sm:flex-row"
          >
            <Link
              data-testid="hero-contact-button"
              to="/kontakt"
              className="inline-flex items-center justify-center gap-2 rounded-sm bg-brand px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-brand-hover"
            >
              Kontakt os <ArrowRight className="h-5 w-5" />
            </Link>
            <Link
              data-testid="hero-quote-button"
              to="/kontakt"
              className="inline-flex items-center justify-center gap-2 rounded-sm border border-white/30 bg-white/10 px-7 py-3.5 text-base font-semibold text-white backdrop-blur transition-colors hover:bg-white/20"
            >
              Indhent et uforpligtende tilbud
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="mx-auto max-w-7xl px-6 py-24">
        <div className="max-w-2xl">
          <span className="text-sm font-semibold uppercase tracking-widest text-brand">Hvorfor SB Logistik</span>
          <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
            Kvalitet og service i hvert eneste led
          </h2>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
              data-testid={`feature-card-${i}`}
              className="group rounded-sm border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-slate-300"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-sm bg-brand/10 text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                <f.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-5 font-heading text-lg font-semibold text-slate-900">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SERVICES SPLIT */}
      <section className="bg-slate-50">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 py-24 lg:grid-cols-2">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
            <span className="text-sm font-semibold uppercase tracking-widest text-brand">Vores ydelser</span>
            <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
              Transport tilpasset din virksomhed
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600 md:text-lg">
              Fra enkelte forsendelser til faste kørselsaftaler – vi tilbyder
              fleksible logistikløsninger, der passer til dine behov.
            </p>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {services.map((s) => (
                <li key={s} className="flex items-center gap-3 text-sm font-medium text-slate-700">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-brand" /> {s}
                </li>
              ))}
            </ul>
            <Link
              data-testid="home-services-link"
              to="/ydelser"
              className="mt-9 inline-flex items-center gap-2 rounded-sm bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
            >
              Se alle ydelser <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>

          <motion.div
            initial="hidden" whileInView="show" viewport={{ once: true }} custom={1} variants={fadeUp}
            className="overflow-hidden rounded-sm border border-slate-200"
          >
            <img src={SERVICES_IMG} alt="Flåde af varevogne" className="h-full max-h-[480px] w-full object-cover" />
          </motion.div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="bg-slate-900">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-6 py-16 md:flex-row md:items-center">
          <div>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-white md:text-3xl">
              Klar til en pålidelig transportpartner?
            </h2>
            <p className="mt-3 max-w-xl text-slate-300">
              Kontakt os i dag, eller send os en e-mail på{" "}
              <a data-testid="cta-email-link" href="mailto:info@sb-logistik.dk" className="font-semibold text-white underline decoration-brand underline-offset-4 hover:text-brand">
                info@sb-logistik.dk
              </a>{" "}
              – vi vender tilbage hurtigst muligt.
            </p>
          </div>
          <Link
            data-testid="cta-quote-button"
            to="/kontakt"
            className="inline-flex shrink-0 items-center gap-2 rounded-sm bg-brand px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-brand-hover"
          >
            Indhent et uforpligtende tilbud <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
